import { cn } from "@tellescope/utils";
import { TellescopeFormFieldBaseProps } from "../types";
import { Checkbox, Input, Label } from "@tellescope/ui";
import { Trans, useTranslation } from "react-i18next";
import { useForm } from "../../use-form";
import { useMemo } from "react";

export interface SignatureFieldProps extends TellescopeFormFieldBaseProps<"signature"> {
  fullName: string;
}

export const SignatureField = ({
  title,
  value,
  onChange,
  className,
  options,
  error: providedError,
  onBlur,
  fullName,
}: SignatureFieldProps) => {
  const { t } = useTranslation("forms");
  const form = useForm({
    fields: {
      consent: {
        type: "checkbox",
        initialValue: !!value?.signed,
        required: true,
      },
      signature: {
        type: "text",
        initialValue: value?.fullName ?? "",
        required: true,
      },
    },
    validators: {
      signature: (newVal) => {
        if (!newVal) return undefined;
        return fullName.toLowerCase() === newVal.toLowerCase() ? undefined : t("Signature does not match name");
      },
    },
    sideEffects: (newValues) => {
      onChange({
        ...value,
        signed: !!newValues.signature && !!newValues.consent,
        fullName,
      });
      return {};
    },
    validateOn: "blur",
  });

  const error = providedError || form.getFieldError("signature").message;

  const checkboxProps = useMemo(() => {
    const { value, onValueChange } = form.getCustomFieldProps("consent");
    return {
      checked: value,
      onCheckedChange: onValueChange,
    };
  }, [form]);

  return (
    <div className={cn("space-y-2", className)}>
      {!!title && <h4>{title}</h4>}
      <div className="flex gap-2 items-center">
        <Checkbox {...checkboxProps} />
        <Label className="gap-1">
          <Trans ns="forms">
            I consent to use{" "}
            <a className="underline" href={`/e-signature-terms?name=${options?.esignatureTermsCompanyName ?? ""}`}>
              {/* TODO: implement e-signature-terms page */}
              electronic signatures
            </a>
          </Trans>
        </Label>
      </div>
      <div className="flex flex-col gap-1">
        <Input
          {...form.getFieldProps("signature")}
          placeholder={t("Signature")}
          disabled={!form.values.consent}
          onBlur={() => {
            form.getFieldProps("signature").onBlur();
            onBlur?.();
          }}
          className={cn(!!error && "border-destructive")}
          aria-invalid={!!error}
        />
        <p className={`text-muted-foreground text-sm ${form.values.consent ? "" : "opacity-50"}`}>
          {t("Enter your legal full name to complete the signature")}
        </p>
      </div>
      {!!error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
